import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { trackLessonView } from '@/utils/analytics';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SimpleMarkdown from '@/components/SimpleMarkdown';
import { 
  ArrowLeft,
  Clock, 
  Loader2,
  CheckCircle,
  BookOpen,
  Tag
} from 'lucide-react';
import axios from 'axios';

import { API } from '@/config';
import { getPrefetched, getResolvedData, hasPrefetch } from '@/utils/dataPrefetcher';

// Translation helper for all 9 languages
const texts = {
  lessonNotFound: { en: 'Lesson Not Found', ar: 'الدرس غير موجود', sv: 'Lektion hittades inte', tr: 'Ders Bulunamadı', fa: 'درس یافت نشد', so: 'Casharka lama helin', pl: 'Lekcja nie znaleziona', es: 'Lección no encontrada', de: 'Lektion nicht gefunden' },
  backToLessons: { en: 'Back to Lessons', ar: 'العودة للدروس', sv: 'Tillbaka till lektioner', tr: 'Derslere Dön', fa: 'بازگشت به دروس', so: 'Ku noqo casharada', pl: 'Powrót do lekcji', es: 'Volver a las lecciones', de: 'Zurück zu den Lektionen' },
  back: { en: 'Back', ar: 'العودة', sv: 'Tillbaka', tr: 'Geri', fa: 'بازگشت', so: 'Dib', pl: 'Wstecz', es: 'Atrás', de: 'Zurück' },
  startTest: { en: 'Start Test', ar: 'ابدأ الاختبار', sv: 'Börja testa', tr: 'Teste Başla', fa: 'شروع آزمون', so: 'Bilow Imtixaanka', pl: 'Rozpocznij Test', es: 'Comenzar Prueba', de: 'Test Starten' },
  moreLessons: { en: 'More Lessons', ar: 'المزيد من الدروس', sv: 'Fler lektioner', tr: 'Daha Fazla Ders', fa: 'دروس بیشتر', so: 'Casharo dheeraad ah', pl: 'Więcej lekcji', es: 'Más lecciones', de: 'Mehr Lektionen' },
  lessonDetails: { en: 'Lesson Details', ar: 'تفاصيل الدرس', sv: 'Lektionsinformation', tr: 'Ders Detayları', fa: 'جزئیات درس', so: 'Faahfaahinta Casharka', pl: 'Szczegóły lekcji', es: 'Detalles de la lección', de: 'Lektionsdetails' },
  category: { en: 'Category', ar: 'الفئة', sv: 'Kategori', tr: 'Kategori', fa: 'دسته‌بندی', so: 'Qaybta', pl: 'Kategoria', es: 'Categoría', de: 'Kategorie' },
  lessonNumber: { en: 'Lesson Number', ar: 'رقم الدرس', sv: 'Lektionsnummer', tr: 'Ders Numarası', fa: 'شماره درس', so: 'Nambarka Casharka', pl: 'Numer lekcji', es: 'Número de lección', de: 'Lektionsnummer' },
  status: { en: 'Status', ar: 'الحالة', sv: 'Status', tr: 'Durum', fa: 'وضعیت', so: 'Xaalada', pl: 'Status', es: 'Estado', de: 'Status' },
  browseLessons: { en: 'Browse More Lessons', ar: 'تصفح المزيد من الدروس', sv: 'Bläddra bland fler lektioner', tr: 'Daha Fazla Derse Göz At', fa: 'مرور دروس بیشتر', so: 'Baadho Casharo badan', pl: 'Przeglądaj więcej lekcji', es: 'Ver más lecciones', de: 'Mehr Lektionen durchsuchen' },
};

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const LessonDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const passedLesson = location.state?.lessonData || null;
  const [lesson, setLesson] = useState(() => getResolvedData(`lesson_${id}`) || passedLesson || null);
  const [allLessons, setAllLessons] = useState([]);
  const [loading, setLoading] = useState(() => {
    if (getResolvedData(`lesson_${id}`) || passedLesson) return false;
    if (hasPrefetch(`lesson_${id}`)) return false;
    return true;
  });
  const [error, setError] = useState(null);
  const lang = i18n.language;
  const isRTL = lang === 'ar' || lang === 'fa';
  
  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';
  const FREE_LESSONS_LIMIT = user ? 3 : 2;

  useEffect(() => {
    fetchLesson();
    fetchAllLessons();
    trackLessonView(id);
  }, [id]);

  // تحقق من صلاحية الوصول للدرس
  const [isPaidLesson, setIsPaidLesson] = useState(false);
  useEffect(() => {
    if (allLessons.length > 0 && lesson && !isSubscribed) {
      const currentIndex = allLessons.findIndex(l => l.id === lesson.id);
      setIsPaidLesson(currentIndex >= FREE_LESSONS_LIMIT);
    } else {
      setIsPaidLesson(false);
    }
  }, [allLessons, lesson, isSubscribed]);

  const fetchAllLessons = async () => {
    try {
      const prefetched = await getPrefetched('lessons');
      if (prefetched) {
        setAllLessons(prefetched);
      } else {
        const response = await axios.get(`${API}/lessons`);
        setAllLessons(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    }
  };

  const generateSlugFromTitle = (title) => {
    if (!title) return '';
    const sv = typeof title === 'string' ? title : (title.sv || title.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const fetchLesson = async () => {
    const hasInitialData = !!lesson;
    try {
      // 1. Try hover-based prefetch
      const prefetched = await getPrefetched(`lesson_${id}`);
      if (prefetched) {
        setLesson(prefetched);
        setLoading(false);
        return;
      }
      // 2. Try finding in prefetched list data (by ID or by slug) - wait for prefetch to complete
      const allLessonsData = await getPrefetched('lessons');
      if (allLessonsData && Array.isArray(allLessonsData)) {
        const found = allLessonsData.find(l => l.id === id || generateSlugFromTitle(l.title) === id);
        if (found) {
          setLesson(found);
          setLoading(false);
          return;
        }
      }
      // 3. Fallback to API
      if (!hasInitialData) setLoading(true);
      let response;
      try {
        response = await axios.get(`${API}/lessons/${id}`);
      } catch {
        response = await axios.get(`${API}/lessons/by-slug/${id}`);
      }
      if (response.data) {
        setLesson(response.data);
      }
    } catch (error) {
      console.error('Error fetching lesson:', error);
      if (!hasInitialData) setError('Lesson not found');
    } finally {
      setLoading(false);
    }
  };

  const getLessonTitle = (lesson) => {
    if (lesson.title && typeof lesson.title === 'object') {
      return lesson.title[lang] || lesson.title['en'] || lesson.title['sv'] || 'Untitled';
    }
    return lesson.title || 'Untitled';
  };

  const getLessonContent = (lesson) => {
    if (lesson.content && typeof lesson.content === 'object') {
      return lesson.content[lang] || lesson.content['en'] || lesson.content['sv'] || '';
    }
    return lesson.content || '';
  };

  const getCategoryName = (category) => {
    return t(`lessons_category_${category}`);
  };

  const categoryColors = {
    traffic_rules: 'from-blue-600 to-cyan-600',
    traffic_signs: 'from-purple-600 to-pink-600',
    road_rules: 'from-blue-600 to-indigo-600',
    safety: 'from-green-600 to-emerald-600',
    parking: 'from-orange-600 to-red-600',
    theory: 'from-indigo-600 to-violet-600',
    general: 'from-slate-600 to-slate-700'
  };

  if ((loading && !lesson) || (!lesson && !error)) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100" dir={isRTL ? 'rtl' : 'ltr'}>
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8 mt-20">
          <Card className="p-12 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {getText('lessonNotFound', lang)}
            </h2>
            <Button onClick={() => navigate('/teorilektioner')} className="mt-4">
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              {getText('backToLessons', lang)}
            </Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const generateSlug = (name) => {
    if (!name) return '';
    const sv = typeof name === 'string' ? name : (name.sv || name.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const getLessonPath = () => {
    const langPaths = {
      sv: '/teorilektioner', ar: '/ar/doross', en: '/en/lessons',
      tr: '/tr/dersler', fa: '/fa/dars-ha', so: '/so/casharada',
      de: '/de/lektionen', es: '/es/lecciones', pl: '/pl/lekcje',
    };
    return langPaths[lang] || langPaths.sv;
  };

  const category = lesson.category || 'general';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      <main className="flex-grow container mx-auto px-3 sm:px-4 py-4 sm:py-8 mt-16 sm:mt-20">
        {/* SEO Structured Data - Language-aware */}
        <Helmet>
          <title>{getLessonTitle(lesson)} - {lang === 'ar' ? 'دروس نظرية القيادة' : lang === 'sv' ? 'Teorilektion' : 'Driving Theory Lesson'} | DriveMaster</title>
          <meta name="description" content={`${getLessonTitle(lesson)}: ${getLessonContent(lesson).substring(0, 100)} - DriveMaster`} />
          <meta property="og:title" content={`${getLessonTitle(lesson)} | DriveMaster`} />
          <meta property="og:description" content={`${getLessonTitle(lesson)}: ${getLessonContent(lesson).substring(0, 120)}`} />
          <meta property="og:image" content={lesson.image_url || lesson.thumbnail_url || 'https://drivemaster.se/logo192.png'} />
          <meta property="og:image:alt" content={getLessonTitle(lesson)} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="DriveMaster" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${getLessonTitle(lesson)} | DriveMaster`} />
          <meta name="twitter:image" content={lesson.image_url || lesson.thumbnail_url || 'https://drivemaster.se/logo192.png'} />
          <link rel="canonical" href={`https://drivemaster.se${getLessonPath()}/${generateSlug(lesson.title)}`} />
          {Object.entries({
            sv: 'teorilektioner', ar: 'ar/doross', en: 'en/lessons',
            tr: 'tr/dersler', fa: 'fa/dars-ha', so: 'so/casharada',
            de: 'de/lektionen', es: 'es/lecciones', pl: 'pl/lekcje'
          }).map(([hLang, hPath]) => (
            <link key={hLang} rel="alternate" hrefLang={hLang} href={`https://drivemaster.se/${hPath}/${generateSlug(lesson.title)}`} />
          ))}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "name": getLessonTitle(lesson),
            "headline": getLessonTitle(lesson),
            "description": getLessonContent(lesson).substring(0, 200),
            "image": lesson.image_url || lesson.thumbnail_url,
            "author": { "@type": "Organization", "name": "DriveMaster", "url": "https://drivemaster.se" },
            "publisher": { "@type": "Organization", "name": "DriveMaster" },
            "inLanguage": lang,
            "keywords": `${getLessonTitle(lesson)}, ${lang === 'ar' ? 'دروس القيادة, نظرية, السويد, رخصة' : lang === 'sv' ? 'körkortsteori, lektion, Sverige' : 'driving theory, lesson, Sweden'}`
          })}</script>
        </Helmet>

        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/teorilektioner')}
          className="mb-4 sm:mb-6 hover:bg-slate-200 text-sm sm:text-base"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
          {getText('back', lang)}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              {/* Category Header */}
              <div className={`h-2 sm:h-3 bg-gradient-to-r ${categoryColors[category]}`} />
              
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-3 sm:mb-6 flex-wrap">
                  <Badge className={`bg-gradient-to-r ${categoryColors[category] || categoryColors.general} text-white border-0 px-2 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-semibold shadow-md`}>
                    {getCategoryName(category)}
                  </Badge>
                  {lesson.completed && (
                    <Badge className="bg-green-500/20 text-green-700 border-green-500/30 text-xs sm:text-sm">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t('lessons_completed')}
                    </Badge>
                  )}
                </div>

                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
                  {getLessonTitle(lesson)}
                </h1>

                {/* Lesson Image */}
                {(lesson.image_url || lesson.thumbnail_url) && (
                  <div className="relative h-48 sm:h-64 lg:h-80 rounded-xl sm:rounded-2xl overflow-hidden mb-4 sm:mb-6 shadow-lg sm:shadow-xl">
                    <img 
                      src={lesson.image_url || lesson.thumbnail_url} 
                      alt={getLessonTitle(lesson)}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                )}

                <div className="prose prose-slate max-w-none prose-sm sm:prose-base">
                  <SimpleMarkdown
                    source={getLessonContent(lesson)}
                    className="text-slate-700 text-sm sm:text-base lg:text-lg"
                  />
                </div>

                {/* Action Buttons */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button 
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-sm sm:text-base py-2.5 sm:py-3"
                    onClick={() => navigate('/teoriprov')}
                  >
                    {getText('startTest', lang)}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/teorilektioner')}
                    className="text-sm sm:text-base py-2.5 sm:py-3"
                  >
                    {getText('moreLessons', lang)}
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar - Hidden on mobile, shown on large screens */}
          <div className="hidden lg:block lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" />
                {getText('lessonDetails', lang)}
              </h3>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">
                    {getText('category', lang)}
                  </p>
                  <p className="font-semibold text-slate-900">
                    {getCategoryName(category)}
                  </p>
                </div>

                {lesson.order > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">
                      {getText('lessonNumber', lang)}
                    </p>
                    <p className="font-semibold text-slate-900">
                      {lesson.order}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-slate-500 mb-1">
                    {getText('status', lang)}
                  </p>
                  <div className="flex gap-2">
                    {lesson.isPremium ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        {t('lessons_premium')}
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        {t('lessons_free')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <Button 
                  onClick={() => navigate('/teorilektioner')}
                  variant="outline"
                  className="w-full"
                >
                  {getText('browseLessons', lang)}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LessonDetailPage;
